// MUI-free copy of the inline "spectrum header" block repeated on the old-template pages:
//   <Box className="spectrum-header-wrapper solutions …">
//     <Typography className="spectrum-heading color-black …" component="h2" pb={…}>…</Typography>
//     <Box className="divder-wrapper"><hr className="divider-line [divider-line-left]" /></Box>
//     [<Box className="sub-heading-box" pb={4}><Typography className="spectrum-sub-heading …" component="p">…</Typography></Box>]
//   </Box>
// globals.scss styles everything through the global class names kept here (.spectrum-header-wrapper,
// .spectrum-heading, .spectrum-sub-heading, .divder-wrapper, .divider-line, …); the module only adds what MUI
// itself rendered (Typography body1 base, Box/Typography spacing props).
import {memo} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import styles from './SpectrumHeader.module.scss';

/** MUI spacing steps (1 = 8px), as passed to Box/Typography `pb` / `pt`. */
type Spacing = number;

/** Tags a Typography rendered here: `component` when given; MUI Typography with no variant/component = <p>; MUITypography with no component = <div>. */
export type SpectrumTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div' | 'span';

const px = (step?: Spacing) => (step === undefined ? undefined : step * 8);

const spacingStyle = (pt?: Spacing, pb?: Spacing, style?: CSSProperties): CSSProperties | undefined =>
    pt === undefined && pb === undefined && !style ? undefined : {paddingTop: px(pt), paddingBottom: px(pb), ...style};

const join = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

interface SpectrumSubHeadingProps {
    children: ReactNode;
    /** Classes after `spectrum-sub-heading`. Default `color-black`. */
    className?: string;
    /** Rendered tag (the page's Typography `component`). Default `p`. */
    component?: SpectrumTag;
    /** Typography `pt` / `pb` in MUI spacing steps. */
    pt?: Spacing;
    pb?: Spacing;
}

/** `<Typography className="spectrum-sub-heading …" component="p">` — body1 Typography, MUI-free. */
export const SpectrumSubHeading = memo(({children, className = 'color-black', component: Tag = 'p', pt, pb}: SpectrumSubHeadingProps) => (
    <Tag className={join('spectrum-sub-heading', className, styles.text)} style={spacingStyle(pt, pb)}>
        {children}
    </Tag>
));
SpectrumSubHeading.displayName = 'SpectrumSubHeading';

export interface SpectrumHeaderProps {
    /** Heading content. Omit for a wrapper that only holds sub-heading paragraphs (e.g. `trend-dominate-text-wrapper` + pt={2}). */
    heading?: ReactNode;
    /** Heading tag. Default `h2` (the usual `component="h2"`). Pass `p` for a MUI Typography with no `component`, `div` for MUITypography. */
    headingTag?: SpectrumTag;
    /** Classes after `spectrum-heading`, e.g. `color-black text-align-left`, `color-black our-service-main-heading`. Default `color-black`. */
    headingClassName?: string;
    /** Heading Typography `pt` / `pb` (e.g. pb={2} on the trend-dominate variant). */
    headingPt?: Spacing;
    headingPb?: Spacing;
    /** Classes after `spectrum-header-wrapper`, e.g. `solutions trend-dominate-text-wrapper`. Default `solutions`. */
    wrapperClassName?: string;
    /** Wrapper Box `pt` / `pb`. */
    wrapperPt?: Spacing;
    wrapperPb?: Spacing;
    /** Flat inline styles for the wrapper (stand-in for a flat `sx`). */
    wrapperStyle?: CSSProperties;
    id?: string;
    /** `center` → `.divider-line`; `left` → `.divider-line.divider-line-left`; `false` → no divider. Default `center`. */
    divider?: 'center' | 'left' | false;
    /** Content between the heading and the divider (rare: sub-heading placed above the divider). */
    beforeDivider?: ReactNode;
    /** One sub-heading paragraph rendered after the divider (use `children` for several or for other content). */
    subheading?: ReactNode;
    /** Classes after `spectrum-sub-heading` on `subheading`. Default `color-black`. */
    subheadingClassName?: string;
    /** Tag of `subheading`. Default `p`. */
    subheadingTag?: SpectrumTag;
    /** Wrap `subheading` in `<div class="sub-heading-box">` with pb={subheadingBoxPb}. Default false. */
    subheadingBox?: boolean;
    /** pb of the sub-heading-box in MUI spacing steps. Default 4 (32px), as on every page using it. */
    subheadingBoxPb?: Spacing;
    /** Anything else rendered after the divider / subheading (more SpectrumSubHeading paragraphs, buttons, grids). */
    children?: ReactNode;
}

const SpectrumHeader = ({
    heading,
    headingTag: HeadingTag = 'h2',
    headingClassName = 'color-black',
    headingPt,
    headingPb,
    wrapperClassName = 'solutions',
    wrapperPt,
    wrapperPb,
    wrapperStyle,
    id,
    divider = 'center',
    beforeDivider,
    subheading,
    subheadingClassName,
    subheadingTag,
    subheadingBox = false,
    subheadingBoxPb = 4,
    children
}: SpectrumHeaderProps) => {
    const sub =
        subheading === undefined || subheading === null ? null : (
            <SpectrumSubHeading className={subheadingClassName} component={subheadingTag}>
                {subheading}
            </SpectrumSubHeading>
        );

    return (
        <div id={id} className={join('spectrum-header-wrapper', wrapperClassName)} style={spacingStyle(wrapperPt, wrapperPb, wrapperStyle)}>
            {heading !== undefined && (
                <HeadingTag className={join('spectrum-heading', headingClassName, styles.text)} style={spacingStyle(headingPt, headingPb)}>
                    {heading}
                </HeadingTag>
            )}
            {beforeDivider}
            {divider && (
                <div className="divder-wrapper">
                    <hr className={divider === 'left' ? 'divider-line divider-line-left' : 'divider-line'} />
                </div>
            )}
            {sub &&
                (subheadingBox ? (
                    <div className="sub-heading-box" style={{paddingBottom: px(subheadingBoxPb)}}>
                        {sub}
                    </div>
                ) : (
                    sub
                ))}
            {children}
        </div>
    );
};

export default memo(SpectrumHeader);
